import React from "react";
import { Atom, deref, swap, useAtom } from "@dbeining/react-atom";
import * as Dialog from "@radix-ui/react-dialog";

import { toast } from "react-toastify";
import { cx } from "class-variance-authority";
import { CSSDimension } from "@/flokit/types/css-dimension";
import { TestingContextPageScopeProvider } from "@/flokit/utils/testing-context/page-scope";
type PanelChild = () => React.ReactNode;

interface PanelState {
  content: () => React.ReactNode;
  side: "left" | "right";
  width?: number | string;
  maxWidth?: CSSDimension;
  preventClose?: boolean;
  isOpen: boolean;
}

const PanelAtom = Atom.of<PanelState | null>(null as PanelState);

export const PanelManager = () => {
  const args = useAtom(PanelAtom);
  const PanelComponent = args;
  const overlayZIndex = 4000;
  return !args ? null : (
    <TestingContextPageScopeProvider scope="slideout">
      <Dialog.Root
        open={PanelComponent !== null}
        onOpenChange={() => {
          if (!args.preventClose) {
            PanelService.close();
          } else {
            toast("You have unsaved changes.", {
              type: "error",
              autoClose: 5000,
            });
          }
        }}
      >
        <Dialog.Overlay
          onClick={() => {
            if (!args.preventClose) {
              PanelService.close();
            }
          }}
          style={{
            zIndex: overlayZIndex,
          }}
          className="fixed inset-0 bg-overlay"
        />
        <Dialog.Content
          onPointerDownOutside={(e) => {
            e.preventDefault();

            if (!args.preventClose) {
              PanelService.close();
            } else {
              toast("You have unsaved changes.", {
                type: "error",
                autoClose: 5000,
              });
            }
          }}
          style={{
            //
            zIndex: overlayZIndex + 1,
          }}
          className={cx(
            "fixed",
            PanelComponent?.side === "left" ? "left-0" : "right-0",
            // Hide outline since content is automatically focused when opened
            "outline-none"
          )}
        >
          <div
            style={{
              width: PanelComponent?.width ?? "auto",
              maxWidth: PanelComponent?.maxWidth,
            }}
            // disable the animations on mobile.
            // iOS doesn't handle position fixed transitions well
            // it causes flickering when the panel is opened
            className="flex md:animate-animated md:animate-slideInRight md:animate-faster flex-col h-screen overflow-y-hidden w-full mx-auto"
          >
            {PanelComponent?.content()}
          </div>
        </Dialog.Content>
      </Dialog.Root>
    </TestingContextPageScopeProvider>
  );
};

export const PanelService = {
  open: (
    modal: PanelChild,
    opts: {
      side?: "left" | "right";
      width?: number | string;
      maxWidth?: CSSDimension;
    } = {
      width: 334,
      side: "right",
    }
  ) =>
    swap(PanelAtom, () => {
      return {
        content: modal,
        side: opts.side,
        width: opts.width,
        maxWidth: opts.maxWidth,
        preventClose: false,
        isOpen: true,
      };
    }),

  close: () => swap(PanelAtom, () => null),

  setPreventClose: (value: boolean) => {
    swap(PanelAtom, (state) => ({
      ...state,
      preventClose: value,
    }));
  },

  getIsPanelOpen: () => {
    const args = deref(PanelAtom);
    return !!args?.isOpen;
  },
};
